import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { toggleMenu, displayFormatDate } from "../../utils/helpers";
import PageTitle from "../../components/PageTitle";
import ApproveAccessRequestITForm from "../AssetManagement/ApproveAccessRequestITForm";
import { getApi } from "../../utils/api";
import {
  GET_ALL_REQUESTS_IT,
  API_GET_ACCESS_TYPES,
} from "../../config/Endpoints";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import { ThreeDots } from "react-loader-spinner";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import Badge from "../../components/Badge";
import SortableTable from "../../components/SortableTable";

const ApproveAccessRequestIT = ({ title, description }) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [accessTypes, setAccessTypes] = useState([]);
  const [selectedAccessType, setSelectedAccessType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const accessToken = Cookies.get("accessToken");
  const userData = localStorage.getItem("userData");
  const employee = userData ? JSON.parse(userData) : null;
  const employeeId = employee?.id;

  const requestStatusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
    { value: "DEALLOCATED", label: "Deallocated" },
  ];

  const columns = [
    {
      key: "fullName",
      label: "Employee Name",
    },
    {
      key: "accessType",
      label: "Access Type",
      render: (row) => row.accessType?.name || "-",
      compare: (a, b) => {
        const nameA = a.accessType?.name || "";
        const nameB = b.accessType?.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      key: "type",
      label: "Role",
      render: (row) => row.type || "-",
    },
    {
      key: "projectName",
      label: "Project Name",
      render: (row) => (row.projectName ? row.projectName : "All Projects"),
    },
    {
      key: "dlName",
      label: "Delivery Lead",
    },
    {
      key: "reason",
      label: "Reason",
    },
    {
      key: "requestedStartDate",
      label: "Requested Start Date",
      render: (row) => (row.createdAt ? displayFormatDate(row.createdAt) : "-"),
    },
    {
      key: "requestedEndDate",
      label: "Requested End Date",
      render: (row) =>
        row.requestedEndDate
          ? displayFormatDate(row.requestedEndDate)
          : "Ongoing",
    },
    {
      key: "secondApproveStatus",
      label: "Status",
      render: (row) => (
        <Badge
          type={
            row.secondApproveStatus === "PENDING"
              ? "warning"
              : row.secondApproveStatus === "REJECTED"
              ? "danger"
              : row.secondApproveStatus === "DEALLOCATED"
              ? "secondary"
              : "success"
          }
          className="text-capitalize"
        >
          {row.secondApproveStatus === "PENDING"
            ? "Pending"
            : row.secondApproveStatus === "REJECTED"
            ? "Rejected"
            : row.secondApproveStatus === "DEALLOCATED"
            ? "Deallocated"
            : "Approved"}
        </Badge>
      ),
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      render: (row) =>
        ["APPROVED", "REJECTED", "DEALLOCATED"].includes(
          row.secondApproveStatus
        ) ? (
          "-"
        ) : (
          <a
            href="#"
            data-bs-toggle="modal"
            data-bs-target="#staticBackdropAccessRequest"
            className="text-primary text-decoration-none"
            onClick={() => setSelectedRequest(row)}
          >
            View
          </a>
        ),
    },
  ];

  useEffect(() => {
    const fetchAccessTypes = async () => {
      try {
        const res = await getApi(
          `${API_GET_ACCESS_TYPES}?status=APPROVED`,
          accessToken
        );
        if (res.status === 200) {
          const options = res.data.data.accessTypes.map((type) => ({
            value: type.accessId,
            label: type.name,
          }));
          setAccessTypes(options);
        }
      } catch (err) {
        console.error("Error fetching access types:", err);
      }
    };
    fetchAccessTypes();
  }, []);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const status = selectedStatus || "";
      const accessType = selectedAccessType?.value || "";
      const response = await getApi(
        `${GET_ALL_REQUESTS_IT}/${employeeId}?status=${status}&accessType=${accessType}&pageSize=${limit}&pageNo=${currentPage}`,
        accessToken
      );

      if (response.data?.statusCode === 200) {
        const data = Array.isArray(response.data.data?.content)
          ? response.data.data.content
          : [];
        setFilteredItems(data);
        setTotalPages(response.data.data?.totalPages || 1);
        setTotalElements(response.data.data.totalElements || 0);
      } else {
        setFilteredItems([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching IT requests:", error);
      setFilteredItems([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line
  }, [selectedStatus, selectedAccessType, limit, currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const displayedItems =
    searchTerm.trim() === ""
      ? filteredItems
      : filteredItems.filter((item) =>
          (item.fullName || item.employeeName || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );

  return (
    <div className="wrapper-body">
      <div className="page-heading">
        <div id="showMenuBtn" className="collapse-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <PageTitle title={title} description={description} />
        <div className="total">
          Total Access Requests: <span>{totalElements}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="search">
          <Select
            className=" col-md-12 col-sm-12"
            classNamePrefix="react-select"
            options={accessTypes}
            value={selectedAccessType}
            onChange={setSelectedAccessType}
            placeholder="Select Access Type"
            isClearable
          />
        </div>
        <div className="search">
          <Select
            className=" col-md-12 col-sm-12"
            classNamePrefix="react-select"
            options={requestStatusOptions}
            value={
              requestStatusOptions.find(
                (status) => status.value === selectedStatus
              ) || null
            }
            onChange={(option) =>
              setSelectedStatus(option ? option.value : null)
            }
            placeholder="Filter by Status"
            isClearable
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="centered-loader">
          <ThreeDots height="100" width="100" color="blue" />
        </div>
      ) : displayedItems.length > 0 ? (
        <>
          <SortableTable
            columns={columns}
            data={displayedItems}
            currentPage={currentPage}
            limit={limit}
          />

          {/* Pagination & Limit */}
          <div className="row mt-3">
            <div className="col-md-3">
              <label htmlFor="itemsPerPage">Items per page </label>
              <select
                id="itemsPerPage"
                onChange={handleLimitChange}
                value={limit}
                className="border ms-3 w-25 p-2"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
              </select>
            </div>
            <div className="col-md-9">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </>
      ) : (
        <NoRecordsAvailable />
      )}

      {/* Modal */}
      <div
        className="modal fade bd-example-modal-lg"
        id="staticBackdropAccessRequest"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            {selectedRequest && (
              <ApproveAccessRequestITForm
                accessId={selectedRequest.id}
                header="Approve Access Request (IT)"
                onStatusUpdate={(id, status) => {
                  setFilteredItems((prev) =>
                    prev.map((item) =>
                      item.id === id
                        ? { ...item, secondApproveStatus: status }
                        : item
                    )
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApproveAccessRequestIT;
